(() => {
  const clean = (value) => (value || '').replace(/\s+/g, ' ').trim();
  const escapeHtml = (value) =>
    clean(value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[char]);
  const present = (value) => value !== null && value !== undefined && clean(String(value)) !== '';
  const compactNumber = (value) => clean(String(value)).replace(/\.0$/, '');
  const normalizedMeasurement = (value) => {
    const original = clean(String(value));
    const candidate = original.replace(/\b(?:Carbs|KH|Koffein)\b/gi, '').trim();
    const match = candidate.match(/^(-?\d+(?:[.,]\d+)?)(?:\s*[-–]\s*(-?\d+(?:[.,]\d+)?))?\s*(mg|ml|kg|g|l)?$/i);
    if (!match) return { matched: false, text: original, unit: '' };
    const first = compactNumber(match[1].replace(',', '.'));
    const second = match[2] ? compactNumber(match[2].replace(',', '.')) : '';
    return {
      matched: true,
      text: second ? `${first}–${second}` : first,
      unit: clean(match[3] || ''),
    };
  };
  const withUnit = (value, unit) => {
    if (!present(value)) return '';
    const cleanUnit = clean(unit);
    const normalized = normalizedMeasurement(value);
    if (!normalized.matched) return normalized.text;
    const resolvedUnit = normalized.unit || cleanUnit;
    return `${normalized.text}${resolvedUnit ? ` ${resolvedUnit}` : ''}`;
  };
  const qualifierValue = (qualifier, fallbackUnit = '') => {
    if (!qualifier || qualifier.operator !== '<' || !present(qualifier.threshold)) return '';
    const raw = clean(String(qualifier.raw || ''));
    if (raw.startsWith('<')) return raw;
    const threshold = compactNumber(qualifier.threshold).replace('.', ',');
    const unit = clean(String(qualifier.unit || fallbackUnit));
    return `<${threshold}${unit ? ` ${unit}` : ''}`;
  };
  const parseJson = (node) => {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (_error) {
      return null;
    }
  };

  const initTabs = (root) => {
    const tabs = [...root.querySelectorAll('[role="tab"]')];
    const panels = [...root.querySelectorAll('[role="tabpanel"]')];
    if (!tabs.length || !panels.length) return;

    const activate = (tab, focus = false) => {
      tabs.forEach((item) => {
        const active = item === tab;
        item.setAttribute('aria-selected', active ? 'true' : 'false');
        item.tabIndex = active ? 0 : -1;
      });

      panels.forEach((panel) => {
        panel.hidden = panel.dataset.fbPanel !== tab.dataset.fbTab;
      });

      if (focus) tab.focus();
    };

    tabs.forEach((tab) => {
      tab.tabIndex = tab.getAttribute('aria-selected') === 'true' ? 0 : -1;
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', (event) => {
        const current = tabs.indexOf(tab);
        let next = null;

        if (event.key === 'ArrowRight') next = tabs[(current + 1) % tabs.length];
        if (event.key === 'ArrowLeft') next = tabs[(current - 1 + tabs.length) % tabs.length];
        if (event.key === 'Home') next = tabs[0];
        if (event.key === 'End') next = tabs[tabs.length - 1];

        if (!next) return;
        event.preventDefault();
        activate(next, true);
      });
    });
  };

  const keyfactValue = (type, data, isDrink = false) => {
    if (!data) return '';
    if (type === 'carbs') return withUnit(data.carbs, 'g');
    if (type === 'caffeine') {
      if (!present(data.caffeine)) return '';
      if (Number(data.caffeine) === 0) return 'Koffeinfrei';
      return withUnit(data.caffeine, 'mg');
    }
    if (type === 'sodium') return withUnit(data.sodium, 'mg');
    if (type === 'protein') {
      return withUnit(data.protein, 'g') || qualifierValue(data.qualifiers?.protein_g_portion, 'g');
    }
    if (type === 'consistency') return present(data.consistency) ? clean(String(data.consistency)) : '';
    if (type === 'intensity') return present(data.intensity) ? clean(String(data.intensity)) : '';
    if (type === 'portion') return withUnit(data.portion, data.unit || '');
    return '';
  };

  const renderKeyfacts = (root, variantId) => {
    const json = root.querySelector('[data-fb-keyfacts-json]');
    if (!json) return;

    let data;
    try {
      data = JSON.parse(json.textContent || '{}');
    } catch (_error) {
      return;
    }

    const variant = data[String(variantId)] || data[String(root.dataset.initialVariantId)];
    const isDrink = root.dataset.fbDrink === 'true';
    const minimumItems = Number(root.dataset.fbMinItems || 2);
    let visible = 0;

    root.querySelectorAll('[data-fb-keyfact-item]').forEach((item) => {
      const value = keyfactValue(item.dataset.fbKeyfactItem, variant, isDrink);
      const target = item.querySelector('[data-fb-keyfact-value]');
      if (target) target.textContent = value;
      item.hidden = !value;
      if (value) visible += 1;
    });

    root.hidden = visible < minimumItems;
  };

  const initKeyfacts = (root) => {
    renderKeyfacts(root, root.dataset.initialVariantId);
  };

  const propertyLabels = {
    vegan: 'Vegan',
    vegetarian: 'Vegetarisch',
    gluten_free: 'Glutenfrei',
    lactose_free: 'Laktosefrei',
  };

  const updateBadge = (badge, state, label) => {
    badge.classList.remove('fb-pdp-prop-badge--green', 'fb-pdp-prop-badge--neutral');

    if (state === null || state === undefined || state === '') {
      badge.hidden = true;
      badge.textContent = '';
      return;
    }

    badge.hidden = false;
    badge.classList.add(state === true ? 'fb-pdp-prop-badge--green' : 'fb-pdp-prop-badge--neutral');
    badge.textContent = `${state === true ? '✓' : '×'} ${label}`;
  };

  const updateCaffeineBadge = (badge, value) => {
    badge.classList.remove('fb-pdp-prop-badge--green', 'fb-pdp-prop-badge--neutral');

    if (!present(value)) {
      badge.hidden = true;
      badge.textContent = '';
      return;
    }

    const caffeine = Number(value);
    badge.hidden = false;

    if (!Number.isNaN(caffeine) && caffeine > 0) {
      badge.classList.add('fb-pdp-prop-badge--green');
      badge.textContent = `✓ ${compactNumber(caffeine)} mg Koffein`;
      return;
    }

    if (caffeine === 0) {
      badge.classList.add('fb-pdp-prop-badge--neutral');
      badge.textContent = '✓ Koffeinfrei';
      return;
    }

    badge.hidden = true;
    badge.textContent = '';
  };

  const renderProperties = (root, variantId) => {
    const data = parseJson(root.querySelector('[data-fb-properties-json]'));
    if (!data) return;

    const variant = data[String(variantId)] || data[String(root.dataset.initialVariantId)];
    if (!variant) return;

    Object.keys(propertyLabels).forEach((key) => {
      root.querySelectorAll(`[data-fb-prop="${key}"]`).forEach((badge) => {
        updateBadge(badge, variant[key], propertyLabels[key]);
      });
    });

    root.querySelectorAll('[data-fb-prop="caffeine"]').forEach((badge) => {
      updateCaffeineBadge(badge, variant.caffeine_mg);
    });
  };

  const initProperties = (root) => {
    renderProperties(root, root.dataset.initialVariantId);
  };

  const renderTitleBlock = (html, productId) => {
    if (!html || typeof html.querySelector !== 'function') return;
    const nextRoot = html.querySelector('[data-fb-pdp-title]');
    if (!nextRoot) return;

    document.querySelectorAll('[data-fb-pdp-title]').forEach((currentRoot) => {
      if (productId && currentRoot.dataset.productId !== String(productId)) return;
      currentRoot.replaceWith(nextRoot.cloneNode(true));
    });
  };

  const extractReviews = (source) => {
    if (!source) return [];

    return [...source.querySelectorAll('.jdgm-rev')]
      .map((review) => {
        const ratingNode = review.querySelector('.jdgm-rev__rating');
        const rating = ratingNode ? ratingNode.querySelectorAll('.jdgm--on').length : 0;
        return {
          rating,
          date: clean(review.querySelector('.jdgm-rev__timestamp')?.textContent),
          title: clean(review.querySelector('.jdgm-rev__title')?.textContent),
          body: clean(review.querySelector('.jdgm-rev__body')?.textContent),
          author: clean(review.querySelector('.jdgm-rev__author')?.textContent),
          verified: !!review.querySelector('.jdgm-rev__buyer-badge, .jdgm-rev__buyer-badge-wrapper'),
        };
      })
      .filter((review) => review.rating > 0 && (review.body || review.title));
  };

  const renderStars = (rating) =>
    Array.from({ length: 5 }, (_, index) => {
      const filled = index < rating;
      return `<span class="fb-star ${filled ? 'fb-star--filled' : 'fb-star--empty'}" aria-hidden="true">★</span>`;
    }).join('');

  const updateReviewDistribution = (section, reviews) => {
    const total = reviews.length;
    if (!total) return;

    const counts = reviews.reduce((result, review) => {
      const rating = Math.max(1, Math.min(5, Math.round(review.rating)));
      result[rating] = (result[rating] || 0) + 1;
      return result;
    }, {});

    section.querySelectorAll('[data-fb-review-bar]').forEach((row) => {
      const rating = Number(row.dataset.fbReviewBar);
      const percent = Math.round(((counts[rating] || 0) / total) * 100);
      const fill = row.querySelector('.fb-pdp-reviews__bar-fill');
      const label = row.querySelector('.fb-pdp-reviews__bar-pct');
      if (fill) fill.style.width = `${percent}%`;
      if (label) label.textContent = `${percent}%`;
    });

    const count = section.querySelector('.fb-pdp-reviews__count');
    if (count) count.textContent = `${total} ${total === 1 ? 'Bewertung' : 'Bewertungen'}`;

    const distribution = section.querySelector('[data-fb-review-distribution]');
    if (distribution) distribution.hidden = false;
  };

  const reviewCard = (review) => {
    const article = document.createElement('article');
    article.className = 'fb-pdp-review-card';

    article.innerHTML = `
      <div class="fb-pdp-review-card__header">
        <span class="fb-pdp-reviews__stars" aria-label="${review.rating} von 5 Sternen">${renderStars(review.rating)}</span>
        <span class="fb-pdp-review-card__date">${escapeHtml(review.date || 'Review')}</span>
      </div>
      <h3 class="fb-pdp-review-card__title">“${escapeHtml(review.title || review.body)}”</h3>
      ${review.body && review.body !== review.title ? `<p class="fb-pdp-review-card__body">${escapeHtml(review.body)}</p>` : ''}
      <footer class="fb-pdp-review-card__author">
        <span class="fb-pdp-review-card__author-name">${escapeHtml(review.author || 'FuelBox Kunde')}</span>
        ${review.verified ? '<span class="fb-pdp-review-card__verified">✓ Verifiziert</span>' : ''}
      </footer>
    `;
    return article;
  };

  const initReviews = (section) => {
    const grid = section.querySelector('[data-fb-jdgm-review-grid]');
    const source = section.querySelector('[data-fb-jdgm-review-section-source]');
    const nativeReviews = section.querySelector('[data-fb-review-native]');
    const nativeToggle = section.querySelector('[data-fb-review-toggle]');

    if (nativeReviews && nativeToggle) {
      nativeToggle.addEventListener('click', () => {
        nativeReviews.open = true;
        nativeToggle.setAttribute('aria-expanded', 'true');
        nativeReviews.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      nativeReviews.addEventListener('toggle', () => {
        nativeToggle.setAttribute('aria-expanded', nativeReviews.open ? 'true' : 'false');
      });
    }

    if (!grid || !source) return;

    const render = () => {
      const allReviews = extractReviews(source);
      const reviews = allReviews.slice(0, 3);
      if (!reviews.length) return false;
      grid.replaceChildren(...reviews.map(reviewCard));
      grid.hidden = false;
      updateReviewDistribution(section, allReviews);
      return true;
    };

    if (render()) return;

    const observer = new MutationObserver(() => {
      if (render()) observer.disconnect();
    });
    observer.observe(source, { childList: true, subtree: true });
    window.setTimeout(() => {
      observer.disconnect();
      if (!grid.children.length) section.classList.add('fb-pdp-reviews--native-only');
    }, 6000);
  };

  const init = () => {
    document.querySelectorAll('[data-fb-pdp-tabs]').forEach((root) => {
      initTabs(root);
    });

    document.querySelectorAll('[data-fb-pdp-reviews]').forEach(initReviews);
    document.querySelectorAll('[data-fb-keyfacts]').forEach(initKeyfacts);
    document.querySelectorAll('[data-fb-pdp-tabs]').forEach(initProperties);
  };

  document.addEventListener('variant:update', (event) => {
    const variantId = event.detail?.resource?.id;
    if (!variantId) return;
    const productId = event.detail?.data?.productId || event.target?.dataset?.productId;

    renderTitleBlock(event.detail?.data?.html, productId);

    document.querySelectorAll('[data-fb-keyfacts]').forEach((root) => {
      if (productId && root.dataset.productId !== String(productId)) return;
      renderKeyfacts(root, variantId);
    });

    document.querySelectorAll('[data-fb-pdp-tabs]').forEach((root) => {
      if (productId && root.dataset.productId !== String(productId)) return;
      renderProperties(root, variantId);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
